import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { SettlementView, TitleDetail } from "@cx/contracts";
import { formatBRL, formatDateBR, formatPct, type TitleSide } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { OCCURRENCE_LABEL, SIDE, installmentLabel, titleBadge } from "./labels";
import { SettleDialog, TitleDialog } from "./TitleDialogs";
import { PixPanel } from "../pix/PixPanel";
import { BoletoPanel } from "../bank/BoletoPanel";

export const ReceivablePage = () => <TitlePage side="R" />;
export const PayablePage = () => <TitlePage side="P" />;

/** Ficha da conta: dados, baixas (com estorno), registrar baixa, editar e cancelar. */
function TitlePage({ side }: { side: TitleSide }): ReactElement {
  const s = SIDE[side];
  const { id } = useParams({ strict: false }) as { id: string };
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const canUpdate = can(perms, s.module, "update");
  const [editing, setEditing] = useState(false);
  const [settling, setSettling] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [reversing, setReversing] = useState<SettlementView | null>(null);
  const key = ["titles", companyId, side, "detail", id];
  const title = useQuery({ queryKey: key, queryFn: () => api<TitleDetail>(`${s.api}/${id}`) });
  const changed = () => {
    qc.invalidateQueries({ queryKey: ["titles", companyId, side] });
    qc.invalidateQueries({ queryKey: ["fin-accounts", companyId] });
  };
  const action = (path: string, body: unknown, ok: string) => ({
    mutationFn: () =>
      api(`${s.api}/${id}${path}`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
    onSuccess: () => {
      toast("success", ok);
      changed();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const cancel = useMutation(action("/cancel", undefined, "Conta cancelada"));
  const reverse = useMutation({
    mutationFn: (sid: string) =>
      api(`${s.api}/${id}/settlements/${sid}/reverse`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      toast("success", "Baixa estornada");
      changed();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const t = title.data;
  const open = t && (t.status === "open" || t.status === "partial");
  const managed = !!t?.originType;
  const b = t && titleBadge(t);

  return (
    <>
      <ContentHeader
        title={t ? `${s.noun[0]!.toUpperCase()}${s.noun.slice(1)} · ${t.contactName}` : s.title}
        breadcrumb={["Finanças", s.title, t ? formatDateBR(t.dueDate) : ""]}
        actions={
          t && (
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate({ to: s.path })}
              >
                Voltar
              </button>
              {canUpdate && open && !managed && t.paidCents === 0 && (
                <button type="button" className="btn btn-outline-primary" onClick={() => setEditing(true)}>
                  Editar
                </button>
              )}
              {canUpdate && open && !managed && t.paidCents === 0 && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={cancel.isPending}
                  onClick={() => setCanceling(true)}
                >
                  Cancelar conta
                </button>
              )}
              {canUpdate && open && (
                <button type="button" className="btn btn-primary" onClick={() => setSettling(true)}>
                  {s.settle}
                </button>
              )}
            </div>
          )
        }
      />
      <QueryStatus loading={title.isLoading} error={title.error} />
      {t && b && (
        <>
          <div className="box card p-3 mb-3">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <StatusBadge tone={b.tone}>{b.label}</StatusBadge>
              <span>
                Valor <strong>{formatBRL(t.amountCents)}</strong>
              </span>
              <span>
                {side === "R" ? "Recebido" : "Pago"} <strong>{formatBRL(t.paidCents)}</strong>
              </span>
              <span>
                Saldo <strong data-testid="title-open">{formatBRL(t.openCents)}</strong>
              </span>
            </div>
            {t.originType && t.originLabel && (
              <div className="alert alert-info py-2">
                Gerada por {t.originLabel}
                {t.originType === "sales_order" && t.originId && (
                  <>
                    {" "}
                    (
                    <Link className="alert-link" to="/vendas/pedidos/$id" params={{ id: t.originId }}>
                      abrir pedido
                    </Link>
                    )
                  </>
                )}
                {t.originType === "grouped_billing" && t.originId && (
                  <>
                    {" "}
                    (
                    <Link
                      className="alert-link"
                      to="/financas/faturamento-agrupado/$id"
                      params={{ id: t.originId }}
                    >
                      abrir faturamento
                    </Link>
                    )
                  </>
                )}
                {t.originType === "purchase_order_receipt" && t.originDocumentId && (
                  <>
                    {" "}
                    (
                    <Link
                      className="alert-link"
                      to="/suprimentos/pedidos-de-compra/$id"
                      params={{ id: t.originDocumentId }}
                    >
                      abrir pedido de compra
                    </Link>
                    )
                  </>
                )}
                {t.originType === "service_order" && t.originId && (
                  <>
                    {" "}
                    (
                    <Link
                      className="alert-link"
                      to="/servicos/ordens-de-servico/$id"
                      params={{ id: t.originId }}
                    >
                      abrir ordem de serviço
                    </Link>
                    )
                  </>
                )}
                {t.originType === "contract_billing" && t.originDocumentId && (
                  <>
                    {" "}
                    (
                    <Link
                      className="alert-link"
                      to="/servicos/contratos/$id"
                      params={{ id: t.originDocumentId }}
                    >
                      abrir contrato
                    </Link>
                    )
                  </>
                )}
                . Valor e vencimento seguem o documento; cancele pelo documento.
              </div>
            )}
            <dl className="row mb-0">
              <dt className="col-sm-3">{s.contact}</dt>
              <dd className="col-sm-9">
                {t.contactName}{" "}
                <Link
                  className="small ms-1"
                  to="/financas/ficha-financeira/$id"
                  params={{ id: t.contactId }}
                  search={{ side, status: "all" }}
                >
                  ficha financeira
                </Link>
              </dd>
              <dt className="col-sm-3">Vencimento</dt>
              <dd className="col-sm-9">
                {formatDateBR(t.dueDate)}
                {t.originalDueDate && t.originalDueDate !== t.dueDate && (
                  <span className="text-muted"> (original {formatDateBR(t.originalDueDate)})</span>
                )}
              </dd>
              <dt className="col-sm-3">Emissão / competência</dt>
              <dd className="col-sm-9">
                {formatDateBR(t.issueDate)} / {formatDateBR(t.competenceDate)}
              </dd>
              <dt className="col-sm-3">Histórico</dt>
              <dd className="col-sm-9">{t.description ?? "—"}</dd>
              <dt className="col-sm-3">Ocorrência</dt>
              <dd className="col-sm-9">
                {OCCURRENCE_LABEL[t.occurrence]}
                {installmentLabel(t) && ` · ${installmentLabel(t)}`}
              </dd>
              <dt className="col-sm-3">Categoria</dt>
              <dd className="col-sm-9">{t.categoryName ?? "—"}</dd>
              <dt className="col-sm-3">Forma / conta prevista</dt>
              <dd className="col-sm-9">
                {t.paymentMethodName ?? "—"} / {t.financialAccountName ?? "—"}
              </dd>
              <dt className="col-sm-3">Nº documento / no banco</dt>
              <dd className="col-sm-9">
                {t.documentNumber ?? "—"} / {t.bankNumber ?? "—"}
              </dd>
              {side === "R" && (
                <>
                  <dt className="col-sm-3">Vendedor</dt>
                  <dd className="col-sm-9">{t.sellerName ?? "—"}</dd>
                </>
              )}
              <dt className="col-sm-3">Juros / multa</dt>
              <dd className="col-sm-9">
                {formatPct(t.interestMonthPct4)} ao mês / {formatPct(t.finePct4)}
              </dd>
            </dl>
          </div>
          {side === "R" && <PixPanel title={t} canUpdate={canUpdate} onPaid={changed} />}
          {side === "R" && <BoletoPanel title={t} canUpdate={canUpdate} onPaid={changed} />}
          <div className="box card">
            <h2 className="h6 p-3 mb-0">{side === "R" ? "Recebimentos" : "Pagamentos"}</h2>
            {t.settlements.length === 0 ? (
              <p className="text-muted px-3 pb-3 mb-0">Nenhuma baixa registrada.</p>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Baixas">
                  <thead>
                    <tr>
                      <th scope="col">Data</th>
                      <th scope="col">Conta</th>
                      <th scope="col" className="text-end">
                        Valor
                      </th>
                      <th scope="col" className="text-end">
                        Juros + multa
                      </th>
                      <th scope="col" className="text-end">
                        Desconto
                      </th>
                      <th scope="col" className="text-end">
                        Taxa
                      </th>
                      <th scope="col" className="text-end">
                        Total
                      </th>
                      <th scope="col">
                        <span className="visually-hidden">Ações</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.settlements.map((x) => (
                      <tr key={x.id} className={x.reversedAt ? "text-muted" : undefined}>
                        <td>
                          {formatDateBR(x.paidAt)}
                          {x.reversedAt && <span className="badge text-bg-secondary ms-2">Estornada</span>}
                        </td>
                        <td>{x.financialAccountName}</td>
                        <td className="text-end">{formatBRL(x.amountCents)}</td>
                        <td className="text-end">{formatBRL(x.interestCents + x.fineCents)}</td>
                        <td className="text-end">{formatBRL(x.discountCents)}</td>
                        <td className="text-end">{formatBRL(x.feeCents)}</td>
                        <td className="text-end">{formatBRL(x.paidCents)}</td>
                        <td className="text-end">
                          {canUpdate && !x.reversedAt && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              aria-label={`Estornar baixa de ${formatDateBR(x.paidAt)}`}
                              disabled={reverse.isPending}
                              onClick={() => setReversing(x)}
                            >
                              Estornar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {editing && (
            <TitleDialog side={side} title={t} onClose={() => setEditing(false)} onSaved={changed} />
          )}
          {settling && (
            <SettleDialog
              side={side}
              title={t}
              canDiscount={can(perms, s.module, "settle_discount")}
              onClose={() => setSettling(false)}
              onSaved={changed}
            />
          )}
        </>
      )}
      <ConfirmDialog
        open={canceling}
        title="Cancelar conta"
        message="Cancelar esta conta? Ela sai das contas em aberto e fica na aba Canceladas."
        confirmLabel="Cancelar conta"
        onConfirm={() => {
          cancel.mutate();
          setCanceling(false);
        }}
        onCancel={() => setCanceling(false)}
      />
      <ConfirmDialog
        open={!!reversing}
        title="Estornar baixa"
        message="Estornar esta baixa? O saldo volta para a conta e o lançamento inverso entra no extrato com a data de hoje."
        confirmLabel="Estornar"
        onConfirm={() => {
          if (reversing) reverse.mutate(reversing.id);
          setReversing(null);
        }}
        onCancel={() => setReversing(null)}
      />
    </>
  );
}
